import React, { useCallback, useEffect, useRef, useState } from 'react';
import { CheckCircle2, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { WebsiteHeader } from '../components/Dashboard/WebsiteHeader';
import { PaymentInterruptedState, type PaymentFailureKind } from '../components/Payment/PaymentInterruptedState';
import { useAuth } from '../contexts/AuthContext';
import { verifyTransaction } from '../services/paymentService';
import { trackCriticalEvent } from '../utils/mixpanelTracking';
import { trackPurchase } from '../utils/analytics';
import { trackEventOnce } from '../utils/visitorTracking';
import { triggerInvoiceReceiptEmail } from '../services/emailTriggerService';
import { markPaymentPending, notifyCreditsChanged } from '../utils/creditsSync';
import { recordUnlockReceipt } from '../utils/unlockReceipts';
import { PLAN_PRICES_USD, formatUsd } from '../data/pricingPlans';
import { clearPendingUnlockScanId, readPendingUnlockScanId } from '../hooks/useReportUnlock';
type Status = 'verifying' | 'pending' | 'success' | 'failed';
const MAX_ATTEMPTS = 30; // 30 × 2s ≈ 1 minute
const RETRY_DELAY_MS = 2000;
function getReference(): string | null {
  const params = new URLSearchParams(window.location.search);
  return params.get('reference') || params.get('trxref');
}
function classifyResponseFailure(message: string): PaymentFailureKind {
  if (/abandon|cancel/i.test(message)) return 'cancelled';
  if (/expire/i.test(message)) return 'expired';
  return 'failed';
}
function classifyThrown(message: string): PaymentFailureKind {
  if (/abandon|cancel/i.test(message)) return 'cancelled';
  if (/expire/i.test(message) && !/session|jwt|token/i.test(message)) return 'expired';
  if (/session|log in|sign in|jwt|unauthori[sz]ed|auth/i.test(message)) return 'session';
  if (/declin|insufficient|not successful/i.test(message)) return 'failed';
  // Network drops and unknown errors: we can't say payment failed.
  return 'unconfirmed';
}
export function PaymentCallbackPage() {
  const {
    user,
    loading: authLoading,
    refreshUser
  } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState<Status>('verifying');
  const [failureKind, setFailureKind] = useState<PaymentFailureKind>('failed');
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const startedRef = useRef(false);
  const timerRef = useRef<number | null>(null);
  // Which scan to return to — from the callback URL, else the checkout hand-off.
  // Routing only: unlock state is always read from the scans row.
  const [pendingScanId] = useState(() => new URLSearchParams(window.location.search).get('scan_id') || readPendingUnlockScanId());
  useEffect(() => {
    document.title = 'Payment Verification - Toxic Detector';
    return () => {
      if (timerRef.current) window.clearTimeout(timerRef.current);
    };
  }, []);
  const fail = useCallback((kind: PaymentFailureKind, message: string | null, reference: string | null) => {
    void trackCriticalEvent('Payment_Failed', {
      plan_type: 'unknown',
      failure_reason: kind,
      transaction_reference: reference
    });
    // Single-report checkout: always return to the exact scan. It stays
    // locked there; the report page confirms against the scans row.
    if (pendingScanId) {
      clearPendingUnlockScanId();
      const id = encodeURIComponent(pendingScanId);
      if (kind === 'cancelled' || kind === 'failed' || kind === 'expired') {
        navigate(`/analysis?id=${id}&payment=${kind}`, {
          replace: true
        });
        return;
      }
      if (kind === 'timeout' || kind === 'unconfirmed') {
        navigate(`/analysis?id=${id}&checkout=return`, {
          replace: true
        });
        return;
      }
    }
    setFailureKind(kind);
    setError(message);
    setStatus('failed');
  }, [navigate, pendingScanId]);
  const verifyPayment = useCallback(async (reference: string, attemptNo: number) => {
    setAttempt(attemptNo);
    try {
      const response = await verifyTransaction(reference, {
        scanId: pendingScanId,
        userId: user?.id ?? null
      });
      if (response.status === 'success') {
        setStatus('success');
        const planType = String(response.plan_type || 'unknown');
        const unlockedScanId = response.scan_id || (planType === 'single_scan' ? pendingScanId : null);
        void trackCriticalEvent('Payment_Completed', {
          plan_type: planType,
          amount_paid: response.amount || 0,
          transaction_reference: reference,
          payment_method: 'paystack',
          is_first_purchase: response.is_first_purchase || false
        });
        // GA4 + first-party purchase events fire ONLY here, after the
        // provider confirmed payment. Deduped by reference.
        trackPurchase(reference, planType);
        const listPrice = planType in PLAN_PRICES_USD ? PLAN_PRICES_USD[planType as keyof typeof PLAN_PRICES_USD] : null;
        void trackEventOnce(`purchase_completed:${reference}`, 'purchase_completed', {
          product: planType,
          price: typeof response.amount === 'number' ? response.amount / 100 : listPrice,
          currency: response.data?.currency || 'USD',
          scan_id: unlockedScanId,
          user_id: user?.id ?? null,
          payment_reference: reference
        });

        // Persist the user ↔ scan ↔ payment relationship.
        if (unlockedScanId && user?.id) {
          recordUnlockReceipt({
            user_id: user.id,
            scan_id: unlockedScanId,
            payment_reference: reference
          });
        }
        try {
          const amountStr = response.amount ? formatUsd(response.amount / 100) : formatUsd(PLAN_PRICES_USD.single_scan);
          await triggerInvoiceReceiptEmail(`${window.location.origin}/payment-success`, `${window.location.origin}/dashboard`, amountStr, {
            invoiceNumber: reference,
            planName: response.plan_type || 'Single Scan'
          });
        } catch (emailErr) {
          // Non-blocking — an email issue never fails the payment flow.
          console.error('[PaymentCallback] Failed to queue invoice email:', emailErr);
        }
        markPaymentPending();
        notifyCreditsChanged();
        await refreshUser();
        clearPendingUnlockScanId();

        // Never unlock from this response — the report page polls the scan
        // row and unlocks only when is_unlocked === true.
        timerRef.current = window.setTimeout(() => {
          if (unlockedScanId) {
            navigate(`/analysis?id=${encodeURIComponent(unlockedScanId)}&checkout=return`, {
              replace: true
            });
          } else {
            navigate(`/payment-success?plan=${encodeURIComponent(planType)}`, {
              replace: true
            });
          }
        }, 600);
        return;
      }
      if (response.status === 'pending') {
        if (attemptNo + 1 >= MAX_ATTEMPTS) {
          fail('timeout', 'Payment verification timed out.', reference);
          return;
        }
        setStatus('pending');
        timerRef.current = window.setTimeout(() => {
          void verifyPayment(reference, attemptNo + 1);
        }, RETRY_DELAY_MS);
        return;
      }
      const message = response.message || 'Payment verification failed';
      fail(classifyResponseFailure(message), message, reference);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to verify payment';
      console.error('Verification error:', err);
      fail(classifyThrown(message), message, reference);
    }
  }, [fail, navigate, pendingScanId, refreshUser, user?.id]);

  // Wait for auth to restore after the provider redirect before verifying.
  useEffect(() => {
    if (authLoading || startedRef.current) return;
    startedRef.current = true;
    const reference = getReference();
    if (!reference) {
      fail('missing_reference', 'No payment reference found in URL', null);
      return;
    }
    if (!user?.id) {
      fail('session', 'User not authenticated', reference);
      return;
    }
    void verifyPayment(reference, 0);
  }, [authLoading, user?.id, fail, verifyPayment]);
  const handleRetry = () => {
    const reference = getReference();
    if (!reference) return;
    setStatus('verifying');
    setError(null);
    void verifyPayment(reference, 0);
  };
  if (status === 'failed') {
    return <PaymentInterruptedState kind={failureKind} message={error} scanId={pendingScanId} onRetry={handleRetry} data-id="element-9217" data-mp="3dopkxw2x5spc" />;
  }
  const isSuccess = status === 'success';
  return <div className="flex min-h-screen w-full flex-col bg-[#050505] text-white" data-id="element-9218" data-mp="1jf41gtgm0bh2">
      <WebsiteHeader data-id="element-9219" data-mp="3v27o0gc6zqe5" />
      <main className="mx-auto w-full max-w-md flex-1 px-5 pb-10 pt-28" data-id="element-9220" data-mp="3ttr3jjcm2a6">
        <div role="status" aria-live="polite" className="border border-white/10 bg-[#0a0a0a] p-8 text-center" data-id="element-9221" data-mp="3vwgq2gl9ted">
          {isSuccess ? <CheckCircle2 className="mx-auto text-emerald-400" size={36} aria-hidden="true" data-id="element-9222" data-mp="3q83crkvw8w79" /> : <Loader2 className="mx-auto animate-spin text-[#FF0000]" size={36} aria-hidden="true" data-id="element-9223" data-mp="3c8bu36ufmrmx" />}
          <h1 className="mt-4 text-2xl font-black uppercase tracking-tight" data-id="element-9224" data-mp="3g943c03b0wj7">
            {isSuccess ? 'Payment received' : status === 'pending' ? 'Payment processing' : 'Confirming payment'}
          </h1>
          <p className="mt-2 text-sm leading-relaxed text-gray-400" data-id="element-9225" data-mp="10l41375h1wgp">
            {isSuccess ? pendingScanId ? 'Returning to your report…' : 'Redirecting…' : 'Confirming with our secure payment provider. Please keep this window open.'}
          </p>
          {status === 'pending' && <div className="mt-6" data-id="element-9226" data-mp="1sczrnj778vs0">
              <div className="h-1 w-full overflow-hidden bg-white/10" data-id="element-9227" data-mp="1ekjo8hadyvqk">
                <div className="h-full bg-[#FF0000] transition-[width] duration-300 ease-out" style={{
              width: `${(attempt + 1) / MAX_ATTEMPTS * 100}%`
            }} data-id="element-9228" data-mp="1jiv00xk8rgbf" />
              </div>
              <p className="mt-2 font-mono text-[11px] text-gray-500" data-id="element-9229" data-mp="2baaq51l0bl6e">
                Checking status · attempt {attempt + 1} of {MAX_ATTEMPTS}
              </p>
            </div>}
        </div>
      </main>
    </div>;
}